import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Subject, firstValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CallNotice, CallSignal, CallSocketEvent, CallState } from '../models/call';
import { ChatMember, memberName } from '../models/chat';
import { CallTones } from './call-tones';
import { ChatService } from './chat.service';

// STUN publics et gratuits, utilisés si l'API ne répond pas (pas de relais TURN dans ce cas)
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];
const ICE_SERVERS_TIMEOUT_MS = 4000;
// Coupure réseau passagère : on laisse à WebRTC le temps de se rétablir avant de raccrocher
const DISCONNECTED_GRACE_MS = 10000;

const REASON_NOTICES: { [reason: string]: string } = {
  offline: 'call.offline',
  busy: 'call.busy',
  declined: 'call.declined',
  noAnswer: 'call.noAnswer',
};

// Appels audio/vidéo à deux entre amis, en WebRTC pair à pair. La signalisation passe par le socket du chat
// (ChatService), les médias directement entre navigateurs (ou via le relais TURN fourni par l'API).
// Un seul appel à la fois ; l'interface est le composant global app-call.
@Injectable({
  providedIn: 'root'
})
export class CallService {

  private state = new BehaviorSubject<CallState | null>(null);
  state$ = this.state.asObservable();

  // Messages à afficher à l'utilisateur (clé i18n + paramètres)
  readonly notices$ = new Subject<CallNotice>();

  private peer: RTCPeerConnection | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private iceServers: Promise<RTCIceServer[]> | null = null;
  private disconnectedTimer: ReturnType<typeof setTimeout> | null = null;
  private tones = new CallTones();

  constructor(
    private chatService: ChatService,
    private httpClient: HttpClient,
    private zone: NgZone,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    if (!isPlatformBrowser(platformId)) {
      return;
    }
    this.chatService.callEvents$.subscribe(event => this.zone.run(() => this.onSocketEvent(event)));
    window.addEventListener('pagehide', () => this.hangUp());
  }

  get current(): CallState | null {
    return this.state.value;
  }

  get supported(): boolean {
    return typeof RTCPeerConnection !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  }

  async start(peer: ChatMember, video: boolean): Promise<void> {
    if (this.current) {
      return;
    }
    if (!window.isSecureContext) {
      this.notify('call.insecureContext');
      return;
    }
    if (!this.supported) {
      this.notify('call.unsupported');
      return;
    }
    const callId = crypto.randomUUID();
    this.setState({
      callId, peer, video, status: 'outgoing', muted: false, cameraOff: false, startedAt: null, localStream: null, remoteStream: null,
    });
    this.iceServers = this.loadIceServers();

    const stream = await this.getMedia(video);
    if (!this.isCurrent(callId)) {
      this.stopStream(stream);
      return;
    }
    if (!stream) {
      this.cleanup();
      return;
    }
    this.patch({ localStream: stream });

    const ack = await this.chatService.emitCall('call:invite', { to: peer._id, callId, video }, true);
    if (!this.isCurrent(callId)) {
      return;
    }
    if (!ack?.ok) {
      this.notify(REASON_NOTICES[ack?.reason || ''] || 'call.failed', { pseudo: memberName(peer) });
      this.cleanup();
      return;
    }
    this.tones.play('ringback');
  }

  async accept(): Promise<void> {
    const call = this.current;
    if (!call || call.status !== 'incoming') {
      return;
    }
    this.tones.stop();
    this.patch({ status: 'connecting' });
    this.iceServers = this.loadIceServers();

    const stream = await this.getMedia(call.video);
    if (!this.isCurrent(call.callId)) {
      this.stopStream(stream);
      return;
    }
    if (!stream) {
      this.chatService.emitCall('call:reject', { callId: call.callId });
      this.cleanup();
      return;
    }
    this.patch({ localStream: stream });
    await this.createPeer(stream);
    if (!this.isCurrent(call.callId)) {
      return;
    }
    const ack = await this.chatService.emitCall('call:accept', { callId: call.callId }, true);
    if (this.isCurrent(call.callId) && !ack?.ok) {
      this.notify('call.ended');
      this.cleanup();
    }
  }

  decline(): void {
    const call = this.current;
    if (call?.status === 'incoming') {
      this.chatService.emitCall('call:reject', { callId: call.callId });
      this.cleanup();
    }
  }

  hangUp(): void {
    const call = this.current;
    if (!call) {
      return;
    }
    if (call.status === 'incoming') {
      this.decline();
      return;
    }
    this.chatService.emitCall('call:end', { callId: call.callId });
    this.cleanup();
  }

  toggleMute(): void {
    const call = this.current;
    if (!call?.localStream) {
      return;
    }
    const muted = !call.muted;
    call.localStream.getAudioTracks().forEach(track => track.enabled = !muted);
    this.patch({ muted });
  }

  toggleCamera(): void {
    const call = this.current;
    if (!call?.localStream || !call.video) {
      return;
    }
    const cameraOff = !call.cameraOff;
    call.localStream.getVideoTracks().forEach(track => track.enabled = !cameraOff);
    this.patch({ cameraOff });
  }

  // ----- Signalisation -----

  private onSocketEvent(event: CallSocketEvent): void {
    const call = this.current;
    if (event.type === 'incoming') {
      this.onIncoming(event.callId, event.from, event.video, event.caller);
      return;
    }
    if (!call || (event.type !== 'disconnected' && event.callId !== call.callId)) {
      return;
    }
    switch (event.type) {
      case 'accepted':
        this.onAccepted();
        break;
      case 'signal':
        this.onSignal(event.data);
        break;
      case 'rejected':
        this.notify(REASON_NOTICES[event.reason], { pseudo: memberName(call.peer) });
        this.cleanup();
        break;
      case 'handled':
        // Décroché ou refusé sur un autre appareil
        if (call.status === 'incoming') {
          this.cleanup();
        }
        break;
      case 'ended':
        this.notify(this.endedNotice(call, event.reason), { pseudo: memberName(call.peer) });
        this.cleanup();
        break;
      case 'disconnected':
        this.notify('call.connectionLost');
        this.cleanup();
        break;
    }
  }

  private onIncoming(callId: string, from: string, video: boolean, caller?: ChatMember): void {
    if (this.isCurrent(callId)) {
      return;
    }
    if (this.current) {
      this.chatService.emitCall('call:reject', { callId, reason: 'busy' });
      return;
    }
    // Appli ouverte depuis la notification : la liste d'amis n'est pas encore chargée, d'où l'appelant fourni par le serveur
    const friend = this.chatService.relationsValue?.friends.find(f => String(f.user._id) === String(from));
    const peer: ChatMember = caller ?? friend?.user ?? { _id: from, pseudo: '' };
    this.setState({
      callId, peer, video, status: 'incoming', muted: false, cameraOff: false, startedAt: null, localStream: null, remoteStream: null,
    });
    this.tones.play('ringtone');
  }

  // Côté appelant : l'ami a décroché, on envoie l'offre
  private async onAccepted(): Promise<void> {
    const call = this.current;
    if (!call || call.status !== 'outgoing' || !call.localStream) {
      return;
    }
    this.tones.stop();
    this.patch({ status: 'connecting' });
    const peer = await this.createPeer(call.localStream);
    if (!this.isCurrent(call.callId)) {
      return;
    }
    try {
      await peer.setLocalDescription(await peer.createOffer());
      this.signal({ type: 'description', description: peer.localDescription!.toJSON() });
    } catch (err) {
      console.error('Could not create call offer:', err);
      this.fail();
    }
  }

  private async onSignal(data: CallSignal): Promise<void> {
    const peer = this.peer;
    if (!peer || !data) {
      return;
    }
    try {
      if (data.type === 'description') {
        await peer.setRemoteDescription(data.description);
        if (data.description.type === 'offer') {
          await peer.setLocalDescription(await peer.createAnswer());
          this.signal({ type: 'description', description: peer.localDescription!.toJSON() });
        }
        // Candidats arrivés avant la description distante
        const pending = this.pendingCandidates;
        this.pendingCandidates = [];
        for (const candidate of pending) {
          await peer.addIceCandidate(candidate);
        }
      } else if (data.type === 'candidate') {
        if (peer.remoteDescription) {
          await peer.addIceCandidate(data.candidate);
        } else {
          this.pendingCandidates.push(data.candidate);
        }
      }
    } catch (err) {
      console.error('Call signaling error:', err);
    }
  }

  private signal(data: CallSignal): void {
    const call = this.current;
    if (call) {
      this.chatService.emitCall('call:signal', { callId: call.callId, data });
    }
  }

  // ----- WebRTC -----

  private async createPeer(stream: MediaStream): Promise<RTCPeerConnection> {
    const iceServers = await (this.iceServers || this.loadIceServers());
    const peer = new RTCPeerConnection({ iceServers });
    this.peer = peer;
    stream.getTracks().forEach(track => peer.addTrack(track, stream));

    // Les événements RTCPeerConnection ne passent pas par zone.js : on y revient pour la détection de changements
    peer.onicecandidate = event => {
      if (event.candidate) {
        this.signal({ type: 'candidate', candidate: event.candidate.toJSON() });
      }
    };
    peer.ontrack = event => this.zone.run(() => {
      const remote = event.streams[0] || this.current?.remoteStream || new MediaStream();
      if (!event.streams[0]) {
        remote.addTrack(event.track);
      }
      this.patch({ remoteStream: remote });
    });
    peer.onconnectionstatechange = () => this.zone.run(() => this.onConnectionState(peer));
    return peer;
  }

  private onConnectionState(peer: RTCPeerConnection): void {
    if (peer !== this.peer) {
      return;
    }
    this.clearDisconnectedTimer();
    switch (peer.connectionState) {
      case 'connected':
        this.patch({ status: 'connected', startedAt: this.current?.startedAt ?? Date.now() });
        break;
      case 'disconnected':
        this.disconnectedTimer = setTimeout(() => this.fail(), DISCONNECTED_GRACE_MS);
        break;
      case 'failed':
        this.fail();
        break;
    }
  }

  // Connexion pair à pair impossible (souvent : pas de relais TURN pour ce réseau)
  private fail(): void {
    if (this.current) {
      this.notify('call.connectionFailed');
      this.hangUp();
    }
  }

  private async getMedia(video: boolean): Promise<MediaStream | null> {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: video ? { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } : false,
      });
    } catch (err) {
      console.error('Could not access microphone/camera:', err);
      this.notify(video ? 'call.mediaDeniedVideo' : 'call.mediaDenied');
      return null;
    }
  }

  private async loadIceServers(): Promise<RTCIceServer[]> {
    try {
      const response = await firstValueFrom(this.httpClient
        .get<{ iceServers: RTCIceServer[] }>(environment.host + '/chat/ice-servers')
        .pipe(timeout(ICE_SERVERS_TIMEOUT_MS)));
      return response?.iceServers?.length ? response.iceServers : FALLBACK_ICE_SERVERS;
    } catch {
      return FALLBACK_ICE_SERVERS;
    }
  }

  // ----- État -----

  private endedNotice(call: CallState, reason: string): string {
    if (call.status === 'incoming') {
      return 'call.missed';
    }
    if (reason === 'noAnswer') {
      return 'call.noAnswer';
    }
    return reason === 'disconnected' ? 'call.peerDisconnected' : 'call.ended';
  }

  private cleanup(): void {
    this.tones.stop();
    this.clearDisconnectedTimer();
    if (this.peer) {
      this.peer.onicecandidate = null;
      this.peer.ontrack = null;
      this.peer.onconnectionstatechange = null;
      this.peer.close();
      this.peer = null;
    }
    this.pendingCandidates = [];
    this.iceServers = null;
    this.stopStream(this.current?.localStream ?? null);
    this.setState(null);
  }

  private stopStream(stream: MediaStream | null): void {
    stream?.getTracks().forEach(track => track.stop());
  }

  private clearDisconnectedTimer(): void {
    if (this.disconnectedTimer) {
      clearTimeout(this.disconnectedTimer);
      this.disconnectedTimer = null;
    }
  }

  private isCurrent(callId: string): boolean {
    return this.current?.callId === callId;
  }

  private setState(state: CallState | null): void {
    this.zone.run(() => this.state.next(state));
  }

  private patch(changes: Partial<CallState>): void {
    const call = this.current;
    if (call) {
      this.setState({ ...call, ...changes });
    }
  }

  private notify(key: string, params?: { [key: string]: string }): void {
    this.zone.run(() => this.notices$.next({ key, params }));
  }
}
